import { call, put, take, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { api } from 'services';
import { notificationContainer } from 'utils/notificationContainer';
import {
	exportOrdersRequest,
	exportOrdersSuccess,
	exportOrdersFailure,
	exportReturnsRequest,
	exportReturnsSuccess,
	exportReturnsFailure,
	downloadEmExportFileRequest,
	downloadEmExportFileSuccess,
	setDownloadEmExportFileError,
} from './reducer';
import {
	TEmExportFileRequest,
	TEmExportFileResponse,
	TEmExportRequest,
	TEmExportResponsePayload,
} from './types';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

/** The export is prepared in the background, the file is sent to the requested emails. */
const EXPORT_REQUESTED_MESSAGE = 'The export has been requested. The file will be sent by email.';

function* exportOrdersRequestWorker(action: PayloadAction<TEmExportRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEmExportResponsePayload = yield call(api.emExports.exportOrders, payload);
		yield put(exportOrdersSuccess(response));
		notificationContainer(response?.message ?? EXPORT_REQUESTED_MESSAGE, 'success');
	} catch (error) {
		yield put(exportOrdersFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		}
	}
}

function* exportReturnsRequestWorker(action: PayloadAction<TEmExportRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEmExportResponsePayload = yield call(api.emExports.exportReturns, payload);
		yield put(exportReturnsSuccess(response));
		notificationContainer(response?.message ?? EXPORT_REQUESTED_MESSAGE, 'success');
	} catch (error) {
		yield put(exportReturnsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		}
	}
}

function* downloadEmExportFileRequestWorker(action: PayloadAction<TEmExportFileRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEmExportFileResponse = yield call(api.emExports.downloadEmExportFile, payload);
		yield put(downloadEmExportFileSuccess());
		if (response?.file) {
			yield call(fileDownload, response.file, response.fileName);
		}
	} catch (error) {
		let message = 'Request error';
		if (axios.isAxiosError(error)) {
			// With `responseType: 'blob'` the error body is a Blob, so there is no message to read.
			message =
				error.response?.status === 404
					? 'The download link is not valid or the file is no longer available'
					: (error.message ?? message);
		}
		yield put(setDownloadEmExportFileError(message));
		notificationContainer(message, 'error');
	}
}

export function* emExportsSaga() {
	yield takeEvery(exportOrdersRequest.type, exportOrdersRequestWorker);
	yield takeEvery(exportReturnsRequest.type, exportReturnsRequestWorker);
	yield takeEvery(downloadEmExportFileRequest.type, downloadEmExportFileRequestWorker);
}
